import React, { useState, useRef, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import WizardContainer from '../../components/Wizard/WizardContainer';
import { ApplicationStatus } from '../../types/wizard';
import { usePageTracking, useWizardStepTracking, useStepTimer, useAnalytics } from '../../hooks/useAnalytics';
import { courierApiUrl } from '../../config';
import {
  confirmVideoUpload,
  getApplicantV2ErrorMessage,
  loadApplicantV2Session,
  requestVideoUpload,
  skipVideo,
} from '../../utils/applicantV2';

interface VideoIntroProps {
  applicationStatus: ApplicationStatus;
}

const VideoIntro: React.FC<VideoIntroProps> = ({ applicationStatus }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || searchParams.get('email') || '';

  // Check if running in React Native WebView
  const isReactNative = Boolean((window as any).ReactNativeWebView);

  const [mode, setMode] = useState<'choose' | 'record' | 'upload' | 'preview'>('choose');
  const [isRecording, setIsRecording] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [recordingTime, setRecordingTime] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  usePageTracking('Video Introduction');
  useWizardStepTracking('video-intro', 3, 7);
  const { getTimeSpent } = useStepTimer();
  const { trackVideoRecordStart, trackVideoRecordStop, trackVideoRecordRetake, trackVideoUploadStart, trackVideoUploadComplete, trackVideoUploadError, trackVideoIntroComplete, trackWizardStepComplete } = useAnalytics();
  const attemptNumberRef = useRef(1);

  const videoRef = useRef<HTMLVideoElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  useEffect(() => {
    return () => {
      // Cleanup
      if (timerRef.current) clearInterval(timerRef.current);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const startRecording = async () => {
    // If running in React Native, send message to open native camera
    if (isReactNative) {
      try {
        (window as any).ReactNativeWebView.postMessage(
          JSON.stringify({ type: 'recordVideo' })
        );
        return;
      } catch (err) {
        console.error('Error sending message to React Native:', err);
        setError('Failed to open camera. Please try again.');
        return;
      }
    }

    // Original web-based recording
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: 1280, height: 720 },
        audio: true
      });

      streamRef.current = stream;

      // Set mode first to ensure video element is mounted
      setMode('record');
      setError('');

      // Wait for next tick to ensure video element is in DOM
      await new Promise(resolve => setTimeout(resolve, 100));

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        // Explicitly play the video for mobile browser compatibility
        try {
          await videoRef.current.play();
        } catch (playErr) {
          console.warn('Video play failed:', playErr);
        }
      }

      const supportedMimeType = [
        'video/webm;codecs=vp8,opus',
        'video/webm',
        'video/mp4',
      ].find((type) => MediaRecorder.isTypeSupported(type));
      const mediaRecorder = supportedMimeType
        ? new MediaRecorder(stream, { mimeType: supportedMimeType })
        : new MediaRecorder(stream);

      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = () => {
        const recordedType = mediaRecorder.mimeType.split(';')[0] || 'video/webm';
        const blob = new Blob(chunksRef.current, { type: recordedType });
        setRecordedBlob(blob);
        const url = URL.createObjectURL(blob);
        setPreviewUrl(url);
        setMode('preview');

        // Stop all tracks
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
        }
      };

      mediaRecorder.start();
      setIsRecording(true);
      trackVideoRecordStart(attemptNumberRef.current);
      setRecordingTime(0);

      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          const newTime = prev + 1;
          // Auto-stop at 30 seconds
          if (newTime >= 30) {
            stopRecording();
          }
          return newTime;
        });
      }, 1000);
    } catch (err) {
      console.error('Error accessing camera:', err);
      setError('Could not access camera. Please check permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
      trackVideoRecordStop(recordingTime, attemptNumberRef.current);
      setIsRecording(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!['video/mp4', 'video/quicktime', 'video/webm'].includes(file.type)) {
      setError('Please upload an MP4, MOV, or WebM video.');
      return;
    }

    // Validate file size (50MB max)
    if (file.size > 50 * 1024 * 1024) {
      setError('Video file must be under 50MB.');
      return;
    }

    setUploadedFile(file);
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    setMode('preview');
    trackVideoUploadStart(file.size, file.type);
    setError('');
  };

  const handleRetake = () => {
    trackVideoRecordRetake(recordingTime);
    attemptNumberRef.current += 1;
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setRecordedBlob(null);
    setUploadedFile(null);
    setPreviewUrl('');
    setMode('choose');
  };

  const handleSubmit = async () => {
    const videoBlob = recordedBlob || uploadedFile;
    if (!videoBlob) {
      setError('Please record or upload a video first.');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const v2Session = loadApplicantV2Session();
      const upload = v2Session
        ? await requestVideoUpload(v2Session, videoBlob)
        : (await axios.get(courierApiUrl('/application/video-presigned-url'), { params: { token } })).data;
      const uploadUrl = upload.uploadUrl;
      const videoKey = upload.videoKey;

      // Upload to S3
      await axios.put(uploadUrl, videoBlob, {
        headers: {
          'Content-Type': videoBlob.type || 'video/webm'
        }
      });

      // Confirm upload
      const confirmResponse = v2Session
        ? await confirmVideoUpload(v2Session, videoKey, recordingTime || 0)
        : await axios.post(courierApiUrl('/application/video-confirm'), {
            token,
            videoKey,
            duration: recordingTime || 0,
            fileSize: videoBlob.size
          });

      if (confirmResponse.data.success) {
        trackVideoUploadComplete(videoBlob.size, getTimeSpent());
        trackVideoIntroComplete({ videoType: recordedBlob ? 'recorded' : 'uploaded', videoDuration: recordingTime || undefined, attemptNumber: attemptNumberRef.current });
        trackWizardStepComplete({ stepName: 'video-intro', timeOnStep: getTimeSpent(), completionStatus: 'success' });
        navigate(`/wizard/upload-documents${v2Session ? '' : `?token=${token}`}`);
      } else {
        setError(confirmResponse.data.error || 'Failed to save video');
        setIsSubmitting(false);
      }
    } catch (err) {
      console.error('Video upload error:', err);
      trackVideoUploadError('upload_failed', (err as any).message || 'Unknown error');
      setError(getApplicantV2ErrorMessage(err, 'An error occurred uploading your video. Please try again.'));
      setIsSubmitting(false);
    }
  };

  const handleSkip = async () => {
    setIsSubmitting(true);
    try {
      const v2Session = loadApplicantV2Session();
      if (v2Session) {
        await skipVideo(v2Session);
      } else {
        await axios.post(courierApiUrl('/application/video-confirm'), { token, skipped: true });
      }

      trackWizardStepComplete({ stepName: 'video-intro', timeOnStep: getTimeSpent(), completionStatus: 'skipped' });
      navigate(`/wizard/upload-documents${v2Session ? '' : `?token=${token}`}`);
    } catch (err) {
      console.error('Error skipping video:', err);
      setError(getApplicantV2ErrorMessage(err, 'An error occurred. Please try again.'));
      setIsSubmitting(false);
    }
  };

  return (
    <WizardContainer
      currentStepId="video-intro"
      applicationStatus={applicationStatus}
    >
      <div className="max-w-3xl mx-auto">
        <div className="card p-6 sm:p-8">
          <h1 className="text-2xl font-bold text-charcoal mb-2">
            Video Introduction
          </h1>
          <p className="text-gray-600 mb-6">
            Record a quick 20-30 second video introducing yourself to help us get to know you better.
          </p>

          {/* Benefits banner */}
          <div className="rounded-xl border border-logo-green bg-logo-green/10 p-5 mb-6">
            <div className="flex items-start gap-3">
              <div className="text-2xl">🎥</div>
              <div>
                <p className="font-semibold text-charcoal mb-1">
                  A short introduction helps our team get to know you.
                </p>
                <p className="text-sm text-gray-600">
                  Show us your personality and enthusiasm. Tell us why you want to deliver for Valley Eats!
                </p>
              </div>
            </div>
          </div>

          {/* Choose mode */}
          {mode === 'choose' && (
            <div className="space-y-4">
              <button
                onClick={startRecording}
                className="selection-card group w-full p-5 text-left"
              >
                <div className="flex items-center gap-4">
                  <div className="text-4xl">📹</div>
                  <div>
                    <div className="text-lg font-semibold text-charcoal group-hover:text-logo-green">
                      {isReactNative ? 'Open Camera App' : 'Record Video Now'}
                    </div>
                    <div className="text-sm text-gray-600">
                      {isReactNative
                        ? 'Use your phone camera to record a 20-30 second introduction'
                        : 'Use your camera to record a 20-30 second introduction'}
                    </div>
                  </div>
                </div>
              </button>

              <button
                onClick={() => setMode('upload')}
                className="selection-card group w-full p-5 text-left"
              >
                <div className="flex items-center gap-4">
                  <div className="text-4xl">📤</div>
                  <div>
                    <div className="text-lg font-semibold text-charcoal group-hover:text-logo-green">
                      Upload Pre-Recorded Video
                    </div>
                    <div className="text-sm text-gray-600">
                      Upload a video you've already recorded (max 50MB)
                    </div>
                  </div>
                </div>
              </button>

              {/* Skip button - now visible immediately */}
              <button
                onClick={handleSkip}
                disabled={isSubmitting}
                className="selection-card group w-full p-4 text-center"
              >
                <div className="text-sm font-medium text-gray-500 group-hover:text-gray-600">
                  Skip for now (not recommended)
                </div>
                <div className="text-xs text-gray-400 mt-1">
                  You can continue without a video introduction
                </div>
              </button>
            </div>
          )}

          {/* Recording mode */}
          {mode === 'record' && (
            <div className="space-y-4">
              <div className="relative bg-black rounded-lg overflow-hidden aspect-[3/4] sm:aspect-video">
                <video
                  ref={videoRef}
                  autoPlay
                  muted
                  playsInline
                  className="w-full h-full object-cover"
                />
                {isRecording && (
                  <div className="absolute top-4 left-4 bg-alert-red text-white px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-2">
                    <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
                    Recording {recordingTime}s
                  </div>
                )}
              </div>

              <div className="flex gap-3">
                {isRecording ? (
                  <button
                    onClick={stopRecording}
                    className="flex-1 btn-primary bg-alert-red hover:bg-alert-red/90"
                  >
                    ⏹️ Stop Recording
                  </button>
                ) : null}
                <button
                  onClick={handleRetake}
                  className="flex-1 btn-outline"
                >
                  Cancel
                </button>
              </div>

              <p className="text-sm text-gray-500 text-center">
                Recording will automatically stop at 30 seconds
              </p>
            </div>
          )}

          {/* Upload mode */}
          {mode === 'upload' && (
            <div className="space-y-4">
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                <div className="text-4xl mb-4">📤</div>
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="video/mp4,video/quicktime,video/webm,.mov"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                  <span className="btn-primary inline-block">
                    Choose Video File
                  </span>
                </label>
                <p className="text-sm text-gray-500 mt-3">
                  Accepts MP4, MOV, WebM (max 50MB)
                </p>
              </div>

              <button
                onClick={handleRetake}
                className="w-full btn-outline"
              >
                Back
              </button>
            </div>
          )}

          {/* Preview mode */}
          {mode === 'preview' && (
            <div className="space-y-4">
              <div className="relative bg-black rounded-lg overflow-hidden aspect-[3/4] sm:aspect-video">
                <video
                  src={previewUrl}
                  controls
                  className="w-full h-full object-cover"
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="flex-1 btn-primary disabled:opacity-50"
                >
                  {isSubmitting ? 'Uploading...' : '✓ Submit Video'}
                </button>
                <button
                  onClick={handleRetake}
                  disabled={isSubmitting}
                  className="flex-1 btn-outline disabled:opacity-50"
                >
                  🔄 Retake
                </button>
              </div>

              {recordingTime > 0 && (
                <p className="text-sm text-gray-500 text-center">
                  Duration: {recordingTime} seconds
                  {recordingTime < 20 && (
                    <span className="text-amber-600"> (20-30 seconds recommended)</span>
                  )}
                </p>
              )}
            </div>
          )}

          {error && (
            <div className="mt-6 bg-alert-red/10 border border-alert-red text-alert-red rounded-lg p-4">
              {error}
            </div>
          )}
        </div>
      </div>
    </WizardContainer>
  );
};

export default VideoIntro;
